import { Textarea } from '@mantine/core'
import type { Patient, Reference } from '@medplum/fhirtypes'
import {
  type DateString,
  parseDateString,
  toDateString,
} from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { Button } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import {
  format as formatDate,
  formatISO,
  isWeekend,
  setHours,
  setMinutes,
} from 'date-fns'
import { type JSX, useState } from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

type Props = {
  sdr: SDR
  subject: Reference<Patient>
  currentDueDate: string | undefined
  dateLabel: string
}

export function SDRReviseDueDate({
  sdr,
  subject,
  currentDueDate,
  dateLabel,
}: Props): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const [open, setOpen] = useState(false)
  const [newDate, setNewDate] = useState<DateString | null>(null)
  const [reason, setReason] = useState('')

  const patchMutation = usePatchMutation<SDR>()
  const createComment = useCreateMutation<CommentCommunication>().mutateAsync

  if (isReadOnly) {
    return null
  }

  const handleSave = async (): Promise<void> => {
    if (!newDate || !reason.trim()) {
      return
    }

    const parsed = parseDateString(newDate)
    if (isWeekend(parsed)) {
      return
    }

    const dueDateTime = setMinutes(setHours(parsed, 17), 0)
    // formatISO uses the browser's local offset, matching the existing
    // convention in adjustDateForSDRDeadlineAvoidingWeekends
    const newDueDateISO = formatISO(dueDateTime)

    const oldLabel = currentDueDate
      ? formatDate(new Date(currentDueDate), 'eeee MM/dd/yy')
      : 'not set'
    const newLabel = formatDate(dueDateTime, 'eeee MM/dd/yy')

    // Await patch before creating comment so a failed patch doesn't
    // leave an orphaned activity entry
    await patchMutation.mutateAsync({
      instance: sdr,
      mutateFn: (sdr) => {
        sdr.decisionNotificationDate = newDueDateISO
      },
    })

    await createComment(
      new CommentCommunication({
        message: `<p><strong>${dateLabel} revised</strong>: ${oldLabel} → ${newLabel}</p><p>${reason}</p>`,
        sender: practitioner.reference,
        subject,
        partOf: [sdr.reference],
      })
    )

    setOpen(false)
    setNewDate(null)
    setReason('')
  }

  const initialDate = currentDueDate
    ? toDateString(new Date(currentDueDate))
    : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <IconPencil className="size-3.5" />
          Edit due date
        </Button>
      </PopoverTrigger>
      <PopoverContent className="grid w-80 gap-3 p-4" align="start">
        <div className="text-sm font-medium">Revise Due Date</div>
        <DatePicker
          value={newDate ?? initialDate}
          onChange={setNewDate}
          monthCalendarProps={{
            disabled: [{ dayOfWeek: [0, 6] }],
          }}
        />
        <Textarea
          label="Reason for change"
          placeholder="Explain why the due date is being revised..."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
          autosize
          minRows={2}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setOpen(false)
              setNewDate(null)
              setReason('')
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!newDate || !reason.trim()}
            loading={patchMutation.isPending}
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
